/* eslint-disable max-len */
import React, { useContext } from 'react';
import SwiperCarousel from '@common/SwiperCarousel/SwiperCarousel';
import ImageFallback from '@components/common/Image/ImageFallback';
import { getCdnImageUrl } from '@utils/cloudflare';
import grayHeartIcon from '@assets/images/gray-heart-mobile-view-icon.svg';
import redHeartIcon from '@assets/images/red-heart-mobile-view-icon.svg';
import badgeFire from '@assets/images/badge-fire.svg';
import Image from 'next/legacy/image';
import HotelApi from '@customTypes/hotel-api';
import { getMonthAndYear } from '@helpers/date';
import { store } from '@context/store';
import showClosedForSeasonBanner from '@helpers/showClosedForSeasonBanner';
import dateWithinLastNDays from '@helpers/dateWithinLastNDays';
import stopwatchIcon from '@assets/images/stopwatch-icon.svg';
import useHotSpot from '@hooks/useHotSpot';
import { HotSpotMin } from '@customTypes/hot-spot';

type Slide = {
  key: string;
  url: string;
  imgClassnames: string;
};

type Props = {
  classes?: string;
  name: string;
  mainImage: string;
  navigateToDetailsPage: () => void;
  hotelUrl: string;
  mobileImages?: Slide[];
  compact?: boolean;
  userFavorites: any;
  handleAddingHotelIdToFavorites: any;
  hotel: HotelApi;
  isUserViewingFavoritesInMyAccount: boolean;
  isSRP?: boolean;
};

export default function Gallery({
  classes = '',
  name,
  mainImage,
  mobileImages = [],
  hotelUrl,
  compact = false,
  userFavorites,
  handleAddingHotelIdToFavorites,
  navigateToDetailsPage,
  hotel,
  isUserViewingFavoritesInMyAccount,
  isSRP = false,
}: Props) {
  const globalState = useContext(store);
  const { state } = globalState;
  const { searchDate } = state;

  const renderBadge = (createdAt: string, closedForSeason: boolean) =>
    !closedForSeason && dateWithinLastNDays(createdAt, 90) && !(hotel.hotSpot && isSRP);

  const { getBadgeText } = useHotSpot();

  return (
    <>
      {compact && (
        <>
          <div
            className="w-10 h-10 absolute mt-3 z-40 t-2"
            style={{
              // eslint-disable-next-line no-nested-ternary
              right: compact ? (isUserViewingFavoritesInMyAccount ? '50px' : '22px') : '20px',
            }}
          >
            <Image
              src={
                userFavorites && hotel.id && userFavorites.includes(hotel.id.toString())
                  ? redHeartIcon
                  : grayHeartIcon
              }
              alt="heart"
              onClick={() => handleAddingHotelIdToFavorites(hotel)}
            />
          </div>

          {renderBadge(hotel.createdAt, hotel.closedForSeason) && (
            <div
              className="flex items-center z-10 right-0 mr-8 absolute py-0.5 whitespace-nowrap text-white bg-rp-secondary-orange-dark rounded-l-full rounded-r-full px-3 text-13 tracking-wide font-rp-pn-regular"
              style={{ marginTop: '176px' }}
            >
              <div className="mr-1.5 mt-0.5">
                <Image src={badgeFire} alt="badge-fire" />
              </div>
              <div>Recently Added</div>
            </div>
          )}
          {hotel.closedForSeason && showClosedForSeasonBanner(hotel.reopenDate, searchDate) && (
            <div className="w-full relative flex justify-center">
              <div className="flex flex-col gap-0 items-center z-10 absolute pt-1 pb-0.5 whitespace-nowrap text-rp-primary-black bg-rp-white-faded backdrop-blur-3xl rounded-l-full rounded-r-full text-13 font-rp-pn-semi-bold px-6 tracking-wide mt-3">
                <div className="opacity-100">CLOSED FOR THE SEASON</div>
                {hotel.reopenDate && (
                  <div className="text-rp-dark-gray-2 -mt-0.5">
                    {`Opens ${getMonthAndYear(hotel.reopenDate)}`}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
      <div
        className={`${classes} relative min-h-210px mb-10px d:m-0 w-full flex-shrink-0 d:max-w-429`}
      >
        {compact ? (
          <div className="rounded-xl">
            {hotel.hotSpot && isSRP ? (
              <>
                <div
                  className="absolute bg-solar-surface-brand-ultramarine flex justify-center rounded-xl z-10 -top-6 d:-top-8 d:hidden"
                  style={{ width: 'calc(100% - 3rem)', height: 'calc(100% + 14px' }}
                >
                  <span className="font-rp-pn-light mt-6px d:mt-9.5px text-white font-semibold text-13 d:text-base d:leading-14px leading-14px">
                    Hot Spot Savings
                  </span>
                </div>
                <div
                  className="absolute z-20 bg-white rounded-xl"
                  style={{ width: 'calc(100% - 3rem)', height: 'calc(100% - 10px)' }}
                />
                <div className="absolute bg-white right-9 z-40 bottom-2 rounded-xl bg-solar-surface-brand-ultramarine px-2.5 py-1 h-6 flex items-center gap-5px">
                  <Image src={stopwatchIcon} alt="Stopwatch Icon" />
                  <span className="font-rp-pn-light text-white font-semibold text-13 leading-normal">
                    {getBadgeText(
                      new Date(hotel.hotSpot.booking_applicable_end_date),
                      hotel.hotSpot as HotSpotMin,
                    )}
                  </span>
                </div>
              </>
            ) : null}

            <SwiperCarousel
              slides={mobileImages}
              slidesPerView={3}
              carouselClass={`dlp-carousel ${
                renderBadge(hotel.createdAt, hotel.closedForSeason) ? 'srp-carousel' : ''
              } ${hotel.hotSpot && isSRP ? 'z-30' : ''}`}
              spaceBetween={8}
              alt={name}
              isSRP
              handleClick={navigateToDetailsPage}
              isNavigationEnabled={false}
              startingSlideIndex={1}
            />
          </div>
        ) : (
          <>
            {renderBadge(hotel.createdAt, hotel.closedForSeason) && (
              <div className="flex items-center z-10 right-0 mr-2 absolute py-0.5 whitespace-nowrap text-white bg-rp-secondary-orange-dark rounded-l-full rounded-r-full px-3 text-13 tracking-wide font-rp-pn-regular mt-2">
                <div className="mr-1.5 mt-0.5">
                  <Image src={badgeFire} alt="badge-fire" />
                </div>
                <div>Recently Added</div>
              </div>
            )}
            {hotel.closedForSeason && showClosedForSeasonBanner(hotel.reopenDate, searchDate) && (
              <div
                className={`w-full relative flex justify-center ${
                  renderBadge(hotel.createdAt, hotel.closedForSeason) ? 'mt-8' : ''
                }`}
              >
                <div className="flex flex-col gap-0 items-center z-10 absolute pt-1 pb-0.5 whitespace-nowrap text-rp-primary-black bg-rp-white-faded backdrop-blur-3xl rounded-l-full rounded-r-full text-13 font-rp-pn-semi-bold px-6 tracking-wide mt-3">
                  <div className="opacity-100">CLOSED FOR THE SEASON</div>
                  {hotel.reopenDate && (
                    <div className="text-rp-dark-gray-2 -mt-0.5">
                      {`Opens ${getMonthAndYear(hotel.reopenDate)}`}
                    </div>
                  )}
                </div>
              </div>
            )}
            {hotel.hotSpot && isSRP ? (
              <div className="absolute bg-white right-2 z-10 top-2 rounded-xl bg-solar-surface-brand-ultramarine px-2.5 py-1 h-6 flex items-center gap-5px">
                <Image src={stopwatchIcon} alt="Stopwatch Icon" />
                <span className="font-rp-pn-light text-white font-semibold text-13 leading-normal">
                  {getBadgeText(
                    new Date(hotel.hotSpot.booking_applicable_end_date),
                    hotel.hotSpot as HotSpotMin,
                  )}
                </span>
              </div>
            ) : null}
            <a href={hotelUrl}>
              <ImageFallback
                className="rounded-tl-xl rounded-bl-xl rounded-tr-none rounded-br-none"
                src={getCdnImageUrl(500, `${mainImage}`, '', 100)}
                layout="fill"
                objectFit="cover"
                alt={name}
                title={name}
                loading="eager"
                blurDataUrl={getCdnImageUrl(50, `${mainImage}`, '', 10)}
                onClick={navigateToDetailsPage}
                quality={100}
              />
            </a>
          </>
        )}
      </div>
    </>
  );
}
